// @ts-ignore
import * as THREE from "three";
// @ts-ignore
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
// @ts-ignore
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { gsap } from "gsap";

// Type definitions for modern 3D scenes
interface ModernSceneConfig {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  animationId?: number;
}

interface AnimationConfig {
  bounceDistance: number;
  bounceDuration: number;
  rotationSpeed: number;
  scale: number;
}

// GLB loader function with proper typing and error handling
function loadGLB(scene: THREE.Scene, path: string): Promise<THREE.Group> {
  const loader = new GLTFLoader();

  return new Promise<THREE.Group>((resolve, reject) => {
    loader.load(
      path,
      function (glb: any) {
        const obj = glb.scene;
        scene.add(obj);
        resolve(obj);
      },
      function (xhr: ProgressEvent) {
        // Optional: Show loading progress
        const percentComplete = (xhr.loaded / xhr.total) * 100;
        console.log(`Loading ${path}: ${percentComplete.toFixed(2)}%`);
      },
      function (error: ErrorEvent) {
        console.error(`Error loading ${path}:`, error);
        reject(error);
      }
    );
  });
}

// Optimized bounce animation with modern easing
function createBounceAnimation(
  object: THREE.Object3D,
  config: AnimationConfig
): gsap.core.Timeline {
  const tl = gsap.timeline({ repeat: -1, yoyo: true });

  tl.to(object.position, {
    y: object.position.y + config.bounceDistance,
    duration: config.bounceDuration / 2,
    ease: "power2.inOut",
  });

  return tl;
}

// Optimized rotation animation
// function createRotationAnimation(
//   object: THREE.Object3D,
//   config: AnimationConfig
// ): gsap.core.Timeline {
//   return gsap.to(object.rotation, {
//     y: Math.PI * 2,
//     duration: 360 / config.rotationSpeed,
//     repeat: -1,
//     ease: "none",
//   });
// }

// Modern hero scene with subtle 3D elements
async function buildShopScene(): Promise<ModernSceneConfig> {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);

  // Position camera for better composition with larger view
  camera.position.set(0, 3, 12);
  camera.lookAt(0, 0, 0);

  // Set transparent background
  scene.background = null;

  // Enhanced lighting setup for better visibility
  const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1.5);
  directionalLight.position.set(5, 10, 5);
  directionalLight.castShadow = true;
  directionalLight.shadow.mapSize.width = 2048;
  directionalLight.shadow.mapSize.height = 2048;
  scene.add(directionalLight);

  // Additional fill light for better visibility
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
  fillLight.position.set(-3, 5, 3);
  scene.add(fillLight);

  // Rim light for definition
  const rimLight = new THREE.DirectionalLight(0x4a90e2, 0.6);
  rimLight.position.set(-5, 5, -5);
  scene.add(rimLight);

  // Load 3D model with error handling
  let shop: THREE.Group;
  try {
    shop = await loadGLB(scene, "models/glb/shop.glb");
    shop.scale.set(0.03, 0.03, 0.03);
    shop.position.set(0, -0.5, 0);

    // Enhance material properties for better visibility
    shop.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        // Enhance material properties
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat: any) => {
              mat.roughness = 0.3;
              mat.metalness = 0.1;
              mat.envMapIntensity = 1.0;
            });
          } else {
            child.material.roughness = 0.3;
            child.material.metalness = 0.1;
            child.material.envMapIntensity = 1.0;
          }
        }
      }
    });
  } catch (error) {
    console.warn("Could not load shop model, using fallback");
    // Create a simple fallback geometry with better material
    const geometry = new THREE.BoxGeometry(2, 1, 1);
    const material = new THREE.MeshStandardMaterial({
      color: 0x2563eb,
      roughness: 0.3,
      metalness: 0.1,
    });
    shop = new THREE.Mesh(geometry, material);
    shop.castShadow = true;
    shop.receiveShadow = true;
    scene.add(shop);
  }

  // Load cloud model for atmosphere
  let cloud: THREE.Group;
  try {
    cloud = await loadGLB(scene, "models/glb/cloud.glb");
    cloud.scale.set(0.6, 0.6, 0.6);
    cloud.position.set(-4, 3, -3);
    cloud.rotation.y = Math.PI / 4;
  } catch (error) {
    console.warn("Could not load cloud model, using fallback");
    const geometry = new THREE.SphereGeometry(1, 8, 6);
    const material = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.3,
    });
    cloud = new THREE.Mesh(geometry, material);
    scene.add(cloud);
  }

  // Get canvas element
  const canvas = document.querySelector(".hero .webgl") as HTMLCanvasElement;
  if (!canvas) {
    throw new Error("Hero canvas element not found");
  }

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: "high-performance",
  });

  renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Limit pixel ratio for performance
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.LinearToneMapping; // Use linear tone mapping for better visibility
  renderer.toneMappingExposure = 1.5; // Increase exposure for better visibility
  renderer.outputColorSpace = THREE.SRGBColorSpace; // Ensure proper color space

  // Subtle controls for interaction
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.enablePan = false;
  controls.enableZoom = true;
  controls.minDistance = 8;
  controls.maxDistance = 20;
  controls.maxPolarAngle = Math.PI / 2.2;
  controls.minPolarAngle = Math.PI / 4;
  controls.maxAzimuthAngle = Math.PI / 3;
  controls.minAzimuthAngle = -Math.PI / 3;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.3;

  // Group objects for coordinated animation
  const group = new THREE.Group();
  group.add(shop);
  group.add(cloud);
  scene.add(group);

  // Subtle animations
  const animationConfig: AnimationConfig = {
    bounceDistance: 0.5,
    bounceDuration: 4,
    rotationSpeed: 10,
    scale: 1,
  };

  // Create gentle animations
  createBounceAnimation(group, animationConfig);

  // Gentle rotation for the group
  gsap.to(group.rotation, {
    y: Math.PI * 2,
    duration: 20,
    repeat: -1,
    ease: "none",
  });

  // Responsive handling
  const handleResize = () => {
    const container = canvas.parentElement;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const aspectRatio = rect.width / rect.height;

    camera.aspect = aspectRatio;
    camera.updateProjectionMatrix();

    renderer.setSize(rect.width, rect.height);
  };

  window.addEventListener("resize", handleResize);
  handleResize(); // Initial sizing

  // Animation loop
  const animate = () => {
    controls.update();
    renderer.render(scene, camera);
    return requestAnimationFrame(animate);
  };

  const animationId = animate();

  return {
    scene,
    camera,
    renderer,
    controls,
    animationId,
  };
}

// Shop scene builder with proper typing
async function buildHeroScene(): Promise<void> {
  const shopScene = new THREE.Scene();
  const shop = await loadGLB(shopScene, "models/glb/shop.glb");
  shop.scale.set(0.02, 0.02, 0.02);
  shop.position.y = 1;

  const cloud = await loadGLB(shopScene, "models/glb/cloud.glb");
  cloud.position.x = -2;
  cloud.position.z = -1;

  const camera = new THREE.PerspectiveCamera(45, 3 / 2, 0.1, 1000);
  camera.position.z = 11;
  camera.position.y = 2.5;
  shopScene.add(camera);

  const ambientLight = new THREE.AmbientLight(0xffffff, 1);
  shopScene.add(ambientLight);

  const pointLight = new THREE.PointLight(0xffffff, 500, 1000);
  pointLight.position.set(5, 10, 0);
  shopScene.add(pointLight);

  const pointLightBottom = new THREE.PointLight(0xffffff, 7, 1000);
  pointLightBottom.position.set(-1, -2.5, 5);
  shopScene.add(pointLightBottom);

  const canvas = document.querySelector(".shop") as HTMLCanvasElement;
  if (!canvas) {
    throw new Error("Shop canvas element not found");
  }

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
  });

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.enablePan = false;
  controls.enableZoom = false;

  renderer.render(shopScene, camera);
  renderer.setPixelRatio(2);

  window.addEventListener("resize", () => {
    camera.aspect = 3 / 2;
    camera.updateProjectionMatrix();
  });

  const group = new THREE.Group();
  group.add(shop);
  group.add(cloud);
  shopScene.add(group);
  spinAnimation(group);
  bounceAnimation(group);

  const loop = (): void => {
    renderer.render(shopScene, camera);
    window.requestAnimationFrame(loop);
    controls.update();
  };

  loop();
}

// Optimized scene cleanup
function cleanupScene(config: ModernSceneConfig): void {
  if (config.animationId) {
    cancelAnimationFrame(config.animationId);
  }

  // Dispose of geometries and materials
  config.scene.traverse((object: any) => {
    if (object instanceof THREE.Mesh) {
      object.geometry.dispose();
      if (Array.isArray(object.material)) {
        object.material.forEach((material: any) => material.dispose());
      } else {
        object.material.dispose();
      }
    }
  });

  config.renderer.dispose();
}

// Performance monitoring
function setupPerformanceMonitoring(): void {
  const stats = {
    fps: 0,
    frameCount: 0,
    lastTime: performance.now(),
  };

  const monitor = () => {
    stats.frameCount++;
    const currentTime = performance.now();

    if (currentTime - stats.lastTime >= 1000) {
      stats.fps = Math.round(
        (stats.frameCount * 1000) / (currentTime - stats.lastTime)
      );
      stats.frameCount = 0;
      stats.lastTime = currentTime;

      // Log performance issues
      if (stats.fps < 30) {
        console.warn(`Low FPS detected: ${stats.fps}`);
      }
    }

    requestAnimationFrame(monitor);
  };

  requestAnimationFrame(monitor);
}

// Main scene initialization with error handling
async function initializeModernScenes(): Promise<void> {
  try {
    // Setup performance monitoring
    setupPerformanceMonitoring();

    // Build hero scene
    const heroConfig = await buildHeroScene();

    // Store config for cleanup if needed
    (window as any).heroSceneConfig = heroConfig;

    // 3D scene is ready - main loading screen logic is handled in main-modern.ts
  } catch (error) {
    console.error("Error initializing 3D scenes:", error);

    // 3D scene failed - main loading screen logic is handled in main-modern.ts
  }
}

// Cleanup on page unload
window.addEventListener("beforeunload", () => {
  const heroConfig = (window as any).heroSceneConfig as ModernSceneConfig;
  if (heroConfig) {
    cleanupScene(heroConfig);
  }
});

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeModernScenes);
} else {
  initializeModernScenes();
}

// Bounce animation with proper typing
function bounceAnimation(objectToBounce: THREE.Object3D): void {
  objectToBounce.position.y = -2;

  // Set the initial position
  const initialPosition = objectToBounce.position.clone();

  // Set the bounce distance and duration
  const bounceDistance = 0.6; // Adjust as needed
  const bounceDuration = 3; // Adjust as needed (in seconds)

  // Create the bounce animation
  const bounceAnimation = gsap.timeline({ repeat: -1, yoyo: true });

  bounceAnimation.to(objectToBounce.position, {
    y: initialPosition.y + bounceDistance,
    duration: bounceDuration / 2,
    ease: "power2.inOut",
  });

  bounceAnimation.to(objectToBounce.position, {
    y: initialPosition.y,
    duration: bounceDuration / 2,
    ease: "power2.inOut",
  });

  // Start the animation
  bounceAnimation.play();
}

// Spin animation with proper typing
function spinAnimation(objectToSpin: THREE.Object3D): void {
  // Set the desired rotation speed in degrees per second
  const rotationSpeed = 20; // Adjust as needed (e.g., 90 degrees per second)

  // Create the GSAP animation
  const spinAnimation = gsap.to(objectToSpin.rotation, {
    y: Math.PI * 2, // 360 degrees in radians
    duration: 1 / (rotationSpeed / 360), // Calculate duration based on speed
    repeat: -1, // Repeat indefinitely for continuous spinning
    ease: "linear", // Use a linear ease for constant speed
  });

  // Start the animation
  spinAnimation.play();
}
